<template>
  <div>
    <p v-if="message !== null">
      {{message}}
    </p>

    <template v-if="isEventsVisible">
      <event-preview
        v-for="event in events"
        :key="event.key"
        :playerId="playerId"
        :event="event"
        class="mb-3"
      />
    </template>

    <b-pagination-nav
      v-if="isPaginationVisible"
      :number-of-pages="pageAmount"
      :link-gen="linkGen"
      first-number
      last-number
      use-router
      class="mb-4"
    />

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import { HTTP } from '../http-common';
import notifier from '../notifier';
import LoadingIndicator from './LoadingIndicator.vue';
import EventPreview from './PlayerLevelFinishedEventPreview.vue';
import { addKeys } from '../util';

const PAGE_SIZE = 100;

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PlayerLevelFinishedEvents',

  components: {
    LoadingIndicator,
    EventPreview,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    },

    page: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      loadState: LOAD_STATE_INIT,

      /** @type {?number} */
      eventAmount: null,

      events: null,
    };
  },

  computed: {
    /** @returns {number} */
    offset() {
      const prevPageAmount = this.page - 1;
      return prevPageAmount * PAGE_SIZE;
    },

    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    message() {
      if(this.isLoadedNormal && this.events !== null && this.events.length === 0) {
        return 'No events.';
      }
      return null;
    },

    isEventsVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const events = this.events;
      if(events === null) {
        return false;
      }

      return events.length > 0;
    },

    isPaginationVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      return this.pageAmount > 1;
    },

    /** @returns {number} */
    pageAmount() {
      const eventAmount = this.eventAmount;
      if(eventAmount === null) {
        return 0;
      }

      return Math.ceil(eventAmount / PAGE_SIZE);
    },
  },

  created() {
    this.loadEvents();
  },

  watch: {
    page() {
      this.loadEvents();
    },
  },

  methods: {
    loadEvents() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = `/player/${this.playerId}/level_finished_events`;

      const params = {};

      const offset = this.offset;
      if(offset > 0) {
        params.offset = offset;
      }

      params.limit = PAGE_SIZE;

      HTTP.get(
        url,
        {params: params}
      )
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.eventAmount = data.eventAmount;

        const events = data.events;
        addKeys(events);
        this.events = events;

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err.response.data, {sender: this});
      });
    },

    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },
  },
};
</script>
